/**
 * @file   src\pages\Messaging.js
 * @brief  This file is responsible for handling messaging/chat screen.
 * @date   June, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */

import React, { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import {
  getLocalStorage,
  getUserType,
  loggedInUser,
} from "../../helpers/common";
import { USER_INFO_KEY, USER_TYPE_AGENT } from "../../constants/common";
import DirectMessagesRightSection from "../../components/Messages/DirectMessagesRightSection";
import { loginOrSignUp } from "../../helpers/message";
import IntroDetails from "../../components/Messages/IntroDetails";
import AgentProfileViewModal from "../../components/Introduction/AgentProfileViewModal";
import {
  useNavigate,
  useLocation,
} from "react-router-dom";
import { pageURLs } from "@/constants";
import { getDirectChats } from "../../services/messagesService";
import DirectMessageLeftSection from "../../components/Messages/DirectMessageLeftSection";
import { PageWrapper } from "@/components/PageWrapper";
import { useMLSAgentAtGitchaHomeRedirect } from "@/hooks/useMLSAgentAtGitchaRedirect";

const DIRECT_CHATS_KEY = "directChats";

const DirectMessages = (props) => {

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sessionData = getLocalStorage(USER_INFO_KEY);
  //user's own info
  const userId = _.get(sessionData, "userInfo.userId", "");
  const userDetails = loggedInUser();
  const [showViewIntroductionModal, setViewIntroductionModal] = useState(false);
  const handleIntroDetailModal = (modalState) =>
    setViewIntroductionModal(modalState);
  const [introDetails, setIntroDetails] = useState([]);
  const [showProfileModal, setProfileModal] = useState(false);
  const ProfileModalClose = () => setProfileModal(false);
  const [selectedUserId, setSelectedUserId] = useState(0);
  const [isFromMessage, setIsFromMessage] = useState(false);

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const chatId = params.get("id");
  // The chat panel loads the selected person's profile itself (and leaves
  // the page if that fails), so the page no longer fetches it too.
  const showChatOnMobile = !!chatId;

  // Keyed on the open chat so the list is re-read each time one is opened -
  // rows only get their latest message and time from this call.
  const directChatsKey = [DIRECT_CHATS_KEY, userId, chatId];
  const directChatsQuery = useQuery({
    queryKey: directChatsKey,
    queryFn: async () => {
      const response = await getDirectChats();
      if (response?.errorCode !== 0) {
        throw new Error(response?.message || "Failed to load direct messages");
      }
      const result = _.get(response, "result", null);
      return Array.isArray(result) ? result : [];
    },
    enabled: !!userId,
    staleTime: 0,
    // Drop superseded snapshots so reopening a chat never flashes an older list.
    gcTime: 0,
  });
  // While a reload is in flight, or if it fails, keep showing the last list.
  const lastLoadedChats = useRef([]);
  if (directChatsQuery.data) lastLoadedChats.current = directChatsQuery.data;
  const directChats = directChatsQuery.data ?? lastLoadedChats.current;

  useEffect(() => {
    let email = _.get(userDetails, "userInfo.email", null);
    let login = loginOrSignUp(email);

    if (!login) {
      navigate(pageURLs.home);
    }
  }, []);

  // Called by the chat panel after a send, with the conversation's own id.
  const updateLastMessage = (conversationId, lastMessage) => {
    if (!directChats.some((item) => item.id === conversationId)) {
      // A first message starts a conversation the list doesn't have yet.
      queryClient.invalidateQueries({ queryKey: [DIRECT_CHATS_KEY] });
      return;
    }
    queryClient.setQueryData(directChatsKey, (prev) =>
      (prev ?? directChats).map((item) =>
        item.id === conversationId
          ? { ...item, lastMessage, lastMessageAt: new Date().getTime() }
          : item,
      ),
    );
  };

  const ViewProfile = (agentId, isFromMessageScreen = false) => {
    handleIntroDetailModal(false);
    setIsFromMessage(isFromMessageScreen);
    setSelectedUserId(agentId);
    setProfileModal(true);
  };

  const closeAgentModal = (isFromMessage) => {
    if (!isFromMessage) {
      handleIntroDetailModal(true);
    }
    setProfileModal(false);
  };

  useMLSAgentAtGitchaHomeRedirect();

  return (
    <>
      <PageWrapper>
        <div className="m-0">
          <div className="main-content p-0 agent-no-login-content">
            <div className="msg-tab">
              <Tabs
                defaultActiveKey="direct-message"
                id="uncontrolled-tab-example"
                className="mb-3"
                onSelect={(key) => {
                  if (key === "messages") {
                    navigate("/messages");
                  } else {
                    navigate("/direct-messages");
                  }
                }}
              >
                <Tab eventKey="messages" title="Introduction Messages">
                  Introduction Messgaes
                </Tab>
                <Tab eventKey="direct-message" title="Direct Messages">
                  <div className={`row msg-height ${showChatOnMobile ? "mobile-chat-active" : ""}`}>
                    <div className="col-12 col-md-6 pe-md-3 col-lg-4 pe-lg-3 col-xl-4 pe-xl-2 mb-4 mb-sm-4 mb-md-0 msg-left-col">
                      <div className="ad-strengthen-wrap strengthen-adjust-wrap mh-490 pe-0 ps-0 pt-0 pb-2">
                        <div className="messaging-top-header pb-12 pl-16 pr-16 pt-4">
                          <h3>Messages</h3>
                        </div>

                        <DirectMessageLeftSection messages={directChats} />
                      </div>
                    </div>
                    {chatId && (
                      <DirectMessagesRightSection
                        updateLastMessage={updateLastMessage}
                        userId={userId}
                        onMobileBack={() => {
                          navigate("/direct-messages", { replace: true });
                        }}
                      />
                    )}
                  </div>
                </Tab>
              </Tabs>
            </div>
          </div>
        </div>
      </PageWrapper>
      <IntroDetails
        details={introDetails}
        show={showViewIntroductionModal}
        onHide={handleIntroDetailModal}
        ViewProfileModal={(agentId) => {
          ViewProfile(agentId);
        }}
        userDeleted={false}
      />

      <AgentProfileViewModal
        show={showProfileModal}
        onHide={ProfileModalClose}
        userId={selectedUserId}
        isFromMessage={isFromMessage}
        onClose={(isFromMessage) => {
          closeAgentModal(isFromMessage);
        }}
      />
    </>
  );
};

export default DirectMessages;
